import React, { useEffect, useMemo, useState } from "react";
import { Button, DownloadIcon, Modal, Pagination } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";

import File from "~/components/Icons/NewFiles";
import Notification, {
  objNotofication,
} from "~/components/Notifications/NotificationUploadPage";
import Status from "~/components/Status";
import DownloadForm from "~/pages/Uploads/modals/DownloadForm";
import UploadErrorsList from "~/pages/Uploads/UploadErrorsList";
import Wrapper from "~/shared/ui/Wrapper";
import { NOTIFICATIONS } from "~/constans/notifications";
import { STATUS_TYPES } from "~/constans/statuses";
import { useSetUploads } from "~/entity/Uploads/hooks/useSetUploads";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { hasWarningInErrors } from "~/libs/getData";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  useAcceptUploadDealerValuesMutation,
  useAcceptUploadIndicatorValuesMutation,
  useAcceptUploadSrrTargetMutation,
  useGetNotificationsQuery,
  useGetUploadValuesListQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import { UploadsFilter } from "~/widgets/Filter";
import { UploadDataModal } from "~/widgets/UploadDataModal";

import {
  getUploadsColumns,
  getUploadsSortableApiKeys,
  uploadsSortFieldForQuery,
} from "./model/tableAdapter";

import s from "./Uploads.less";

const Uploads = () => {
  const { t, i18n } = useTranslation();
  const [successNotification, setSuccessNotification] =
    useState<objNotofication>();
  const { hasSomePermission } = usePermissions();
  const setData = useSetLocationData();
  const { handleCount, handlePage } = usePaginationParams();
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    searchField,
    searchText,
    sortField,
    sortType: sortTypeRaw,
    openModal,
    currentId,
  } = useGetLocationData();

  const sortableApiKeys = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getUploadsSortableApiKeys(lang);
  }, [i18n.language]);

  const { sortType: normalizedSortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: sortableApiKeys,
    setData,
  });

  const tableSortField =
    sortField && sortableApiKeys.includes(sortField) ? sortField : undefined;

  const querySortField = uploadsSortFieldForQuery(sortField);

  const columns = useMemo(() => {
    const lang = localStorage.getItem("language");
    return getUploadsColumns(t, sortField, normalizedSortType, lang);
  }, [t, sortField, normalizedSortType, i18n.language]);

  const [notifications, setNotifications] = useState([]);
  const {
    data: { getNotifications },
  } = useGetNotificationsQuery();
  const downloadNotifications = useMemo(
    () =>
      getNotifications.filter(
        ({ type, status }) =>
          type === NOTIFICATIONS.report && status === STATUS_TYPES.CONFIRM,
      ),
    [getNotifications],
  );
  useEffect(() => {
    downloadNotifications.length > 0 && refetch();
  }, [downloadNotifications]);

  const { data, loading, refetch } = useGetUploadValuesListQuery({
    variables: {
      searchText,
      sortType: querySortField ? normalizedSortType : undefined,
      searchField,
      sortField: querySortField,
      page,
      count,
    },
    fetchPolicy: "no-cache",
  });

  const [acceptUploadDealerValuesMutation] =
    useAcceptUploadDealerValuesMutation({
      onError() {
        failFileUpload();
      },
    });

  const [acceptUploadSrrTargetMutation] = useAcceptUploadSrrTargetMutation({
    onError() {
      failFileUpload();
    },
  });
  const { acceptFile, rejectFile } = useSetUploads();
  const [acceptUploadIndicatorValuesMutation] =
    useAcceptUploadIndicatorValuesMutation({
      onError() {
        failFileUpload();
      },
    });

  const rejectUploadIndicatorValuesMutation = async (value: {
    variables: { IndicatorUploadId: string };
  }) => {
    const id = value.variables.IndicatorUploadId;
    if (!id) return;

    const idNum = Number(id);

    rejectFile.mutate(idNum, {
      onSuccess: () => {
        refetch();
      },
      onError: () => {
        failFileUpload();
      },
    });
  };

  const successFileUpload = () => {
    setSuccessNotification({
      id: String(Date.now()),
      type: NOTIFICATIONS.success,
      textNotification: t("ui.file_upload"),
      close: closeNotification,
    });
    refetch();
  };

  const failFileUpload = () => {
    setNotifications([
      {
        id: String(Date.now()),
        type: NOTIFICATIONS.error,
        textNotification: `${t("errors.type")} ${t("errors.no_format")}`,
        close: closeNotification,
      },
      ...notifications,
    ]);
  };

  const rejectUploadIndicatorValues = (id) => {
    rejectUploadIndicatorValuesMutation({
      variables: { IndicatorUploadId: id },
    });
  };

  const makeAcceptHandler = (mutation) => (id, callback) => {
    mutation({ variables: { IndicatorUploadId: id } }).then(() => {
      callback();
      successFileUpload();
    });
  };

  const makeFileHandler = (fileMutation) => (id, callback) => {
    const idNum = Number(id);
    fileMutation.mutate(idNum, {
      onSuccess: () => {
        callback();
        successFileUpload();
      },
      onError: () => {
        failFileUpload();
      },
    });
  };

  const acceptUploadIndicatorValues = makeAcceptHandler(
    acceptUploadIndicatorValuesMutation,
  );
  const acceptUploadDealerValues = makeAcceptHandler(
    acceptUploadDealerValuesMutation,
  );
  const acceptUploadSRRValues = makeAcceptHandler(
    acceptUploadSrrTargetMutation,
  );
  const acceptUploadUserToDealer = makeFileHandler(acceptFile);
  const rejectUploadUserToDealer = makeFileHandler(rejectFile);

  const mutationReject = (type: string) => {
    switch (type) {
      case "indicator":
        return rejectUploadUserToDealer;
      case "userToDealer":
        return rejectUploadUserToDealer;
      case "dealerMetrics":
        return rejectUploadUserToDealer;
      case "target":
        return rejectUploadIndicatorValues;
      case "dealer":
        return rejectUploadIndicatorValues;
    }
  };

  const closeNotification = (id) => {
    setNotifications((prevState) => {
      return prevState.filter((n) => n.id !== id);
    });
  };

  const logFile = (id) => {
    setDataReplace("openModal", "Logs", "currentId", id);
  };

  const uploadsData = get(
    data,
    "IndicatorGetter.getUploadValuesList.items",
    [],
  );

  const uploads = uploadsData.map((upload) => ({
    ...upload,
    createdAt: getFormatDate(upload.createdAt),
    User: upload.User.fullname,
    type: t(`uploads.${upload.type}`),
    typeValue: upload.type,
    logFile,
    status: (
      <Status
        name={upload.status}
        clickable
        onClick={() => logFile(upload.id)}
      />
    ),
    statusValue: upload.status,
    file: (
      <a href={upload.File.link}>
        <File customClassName={s.file_icon} />
      </a>
    ),
  }));

  const total = get(data, "IndicatorGetter.getUploadValuesList.total", 0);

  const mutationResolver = (type: string) => {
    switch (type) {
      case "indicator":
        return acceptUploadUserToDealer;
      case "userToDealer":
        return acceptUploadUserToDealer;
      case "dealerMetrics":
        return acceptUploadUserToDealer;
      case "target":
        return acceptUploadSRRValues;
      case "dealer":
        return acceptUploadDealerValues;
    }
  };

  useEffect(() => {
    if (!loading && uploadsData) {
      const newNotifications = uploads
        .filter((upload) => upload.statusValue === STATUS_TYPES.DRAFT)
        .map((u) => {
          return {
            id: u.id,
            type: hasWarningInErrors(u, t).type,
            textNotification: hasWarningInErrors(u, t).description,
            withConfirmation: true,
            logFile: u.logFile,
            confirm: mutationResolver(u.typeValue),
            cancel: mutationReject(u.typeValue),
          };
        });
      if (successNotification) {
        newNotifications.shift();
        newNotifications.unshift(successNotification);
      }
      setNotifications(newNotifications);
    }
  }, [uploadsData, successNotification]);

  const handleCloseModal = () => {
    setDataReplace("openModal", "", "currentId", "");
  };
  const openUploadModal = () => {
    setDataReplace("openModal", "Upload");
  };
  const openDownloadModal = () => {
    setDataReplace("openModal", "Download");
  };

  const selectedItem = currentId
    ? uploads.filter((el) => el.id === currentId)[0]
    : {};

  const hasUploadButton = hasSomePermission([
    "users.acceptUploadUserToDealer",
    "indicator.values.upload",
    "users.uploadUserToDealer",
    "srr.target.set",
  ]);
  const hasDownloadButton = hasSomePermission([
    "dealergraph.download",
    "indicator.values.download",
  ]);

  return (
    <Wrapper.Content
      title={t("uploads.uploads")}
      filterComponent={<UploadsFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasUploadButton && (
            <Button
              onClick={openUploadModal}
              size={"middle"}
              icon={
                <DownloadIcon
                  size={16}
                  style={{ transform: "rotate(180deg)" }}
                />
              }
            >
              {t("ui.upload")}
            </Button>
          )}
          {hasDownloadButton && (
            <Button
              onClick={openDownloadModal}
              size={"middle"}
              icon={<DownloadIcon size={16} />}
            >
              {t("ui.download")}
            </Button>
          )}
        </Flex>
      }
    >
      <Notification items={notifications} uploadList={uploads} />
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={uploads}
        loading={loading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        sortableColumns={sortableApiKeys}
        sortField={tableSortField}
        sortType={tableSortField ? normalizedSortType : undefined}
        onSortChange={onSortChange}
      />
      <Pagination
        count={Number(count) || 25}
        page={(Number(page) || 0) + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <UploadDataModal
        open={openModal === "Upload"}
        onClose={handleCloseModal}
        refetch={refetch}
      />

      <Modal
        open={openModal === "Download"}
        title={t("uploads.download_data")}
        onCancel={handleCloseModal}
        footer={null}
        closable
        centered
        destroyOnHidden
      >
        <DownloadForm />
      </Modal>

      <Modal
        open={openModal === "Logs"}
        title={`${t("uploads.description")} ${selectedItem?.description || ""}`}
        onCancel={handleCloseModal}
        footer={null}
        closable
        centered
        destroyOnHidden
        size={"large"}
      >
        <UploadErrorsList />
      </Modal>
    </Wrapper.Content>
  );
};

export default Uploads;
